import { type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { AppBackground } from "@/components/app-background";

/**
 * Jornada's OWN auth shell — deliberately NOT the shared `AuthLayout`/`AuthCard`
 * frame that every cloned product inherited (a glass card centred inside a split
 * brand panel). That shared silhouette is exactly what made the products read as
 * clones, so Jornada breaks it.
 *
 * Jornada's identity is a *workforce console*, so its auth reads like a
 * **shift/report sheet**, not a consumer sign-up card:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ ◵ Jornada            [ STATUS · eyebrow ]  │ ← console bar (full width)
 *   │ ░░░░░ blueprint hairline ░░░░░░░░░░░░░░░░░░░░  │
 *   │                                                │
 *   │ ▌ Headline.                                    │ ← left accent rail + title
 *   │ ▌ Supporting line.                             │   (the "sheet" header)
 *   │ ┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄  │
 *   │   email   [____________________]              │ ← form: flat fields on a
 *   │   pass    [____________________]              │   matte field-sheet panel
 *   │   [ Sign in ]                                  │   (no glass card chrome)
 *   └──────────────────────────────────────────────┘
 *
 * There is no two-column brand panel, no GlassSurface card around the form, and
 * no centred-headline editorial column (that is listto-app's shape). Jornada
 * stacks a utilitarian console header over a left-rail "sheet" — a different
 * structure, chrome and hierarchy from any sibling product. The shell stays
 * app-agnostic about the *form*: screens pour their fields in as `children`.
 *
 * ── SCROLL CONTRACT (kit #13, same fix as melodimals #4) ──────────────────
 * `AppBackground` is `position: fixed; overflow: hidden` and clips its children,
 * so it is used here purely as a decorative backdrop SIBLING. The content lives
 * in its own scroll container: `min-h-[100dvh] overflow-y-auto` (never `h-dvh`/
 * `overflow-hidden`), with a `min-h-full` inner column. A tall form (register)
 * or a short landscape viewport is therefore always reachable by scrolling, and
 * the submit button is never clipped.
 */

interface AuthSceneProps {
  /** Status chip on the console bar (e.g. "Welcome back"). Theme-safe. Optional. */
  eyebrow?: string;
  /** Page headline — the work-order title for this screen. Optional for bare states. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the headline. The sheet headline IS the page h1 for
   * Jornada's auth (there is no card title). Pass "h2" only if the screen owns
   * its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** Width of the sheet. "narrow" for login/forgot/reset/verify, "wide" for register. */
  width?: "narrow" | "wide";
  /** The form (fields + actions), rendered on the field-sheet below the header. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

/** The Jornada workday-arc mark, on a solid accent chip. */
function MarkChip() {
  return (
    <span
      class="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95"
      aria-hidden="true"
    >
      <svg
        class="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M4 16.5H20" />
        <path d="M6.5 16.5a5.5 5.5 0 0 1 11 0" />
        <circle cx="12" cy="10.4" r="1.9" fill="currentColor" stroke="none" />
      </svg>
    </span>
  );
}

export function AuthScene(props: AuthSceneProps) {
  return (
    <>
      {/* Decorative fixed backdrop. NEVER the scroll parent. */}
      <AppBackground />

      {/* Real scroll container. NOTE (jornada): global CSS pins
          `html, body { overflow: hidden; height: 100% }` (to suppress portal
          scrollbars), so a `min-h-[100dvh]` container would just grow past the
          clipped viewport and the form bottom would be unreachable. We therefore
          bound the container to the viewport (`h-[100dvh]`) so its OWN
          `overflow-y-auto` engages and the sheet scrolls internally when it does
          not fit (portrait or short landscape). The inner `min-h-full
          justify-center` still centres a short form. */}
      <div
        class="relative z-10 h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-4 sm:px-6"
        style={{
          "padding-top": "max(1.5rem, env(safe-area-inset-top))",
          "padding-bottom": "max(1.5rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1rem, env(safe-area-inset-left))",
          "padding-right": "max(1rem, env(safe-area-inset-right))",
        }}
      >
        <div
          class="mx-auto flex min-h-full w-full flex-col justify-center py-6"
          classList={{
            "max-w-md": (props.width ?? "narrow") === "narrow",
            "max-w-lg": props.width === "wide",
          }}
        >
          {/* Console bar — logo chip + wordmark + per-screen status chip. */}
          <div class="flex items-center justify-between gap-3">
            <Link
              to="/"
              class="group flex items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label="Jornada"
            >
              <MarkChip />
              <span class="text-lg font-bold tracking-tight text-foreground">Jornada</span>
            </Link>
            <Show when={props.eyebrow}>
              <span class="inline-flex items-center gap-1.5 rounded-md bg-accent/12 px-2.5 py-1 font-mono text-[0.7rem] font-semibold uppercase tracking-wider text-accent ring-1 ring-accent/20">
                <span class="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
                {props.eyebrow}
              </span>
            </Show>
          </div>

          {/* Blueprint hairline (dashed): the "field sheet" divider, not a card border. */}
          <div
            class="mt-4 h-px w-full bg-[repeating-linear-gradient(to_right,hsl(var(--border))_0_8px,transparent_8px_14px)]"
            aria-hidden="true"
          />

          {/* The field sheet: matte panel, left accent rail, header + form. */}
          <div class="mt-6 border-l-2 border-accent/60 pl-4 sm:pl-5">
            <Show when={props.headline}>
              <div class="space-y-2">
                <Show
                  when={(props.headingLevel ?? "h1") === "h2"}
                  fallback={
                    <h1 class="text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                      {props.headline}
                    </h1>
                  }
                >
                  <h2 class="text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                    {props.headline}
                  </h2>
                </Show>
                <Show when={props.tagline}>
                  <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                    {props.tagline}
                  </p>
                </Show>
              </div>
              <div class="my-6 h-px w-full bg-border/60" />
            </Show>

            {/* Form lives flat on the sheet (no glass card frame). */}
            <div class="w-full">{props.children}</div>

            <Show when={props.footer}>
              <footer class="mt-6 space-y-3 text-sm">{props.footer}</footer>
            </Show>
          </div>
        </div>
      </div>
    </>
  );
}
